/**
 * GraphQL-backed replacement for useTasksReportData.
 *
 * Uses the `inlineTasks` AGG query (same one powering Tasks Home) instead of the
 * legacy REST endpoint `/rest/inlinetasks/1/task-report`.
 *
 * Pagination: cursor-based ("load more") — the AGG schema does not expose a
 * `totalCount` field on the inlineTasks connection, so page-index pagination is
 * not possible without a backend change. The component layer should render a
 * "Load more" button driven by `hasNextPage`.
 */
import { useCallback, useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';

import GetTasksReportQuery from '../__generated__/GetTasksReportQuery.graphql.ts';
import type {
	GetTasksReportQuery$data,
	GetTasksReportQuery$variables,
} from '../__generated__/GetTasksReportQuery.graphql.ts';
import { buildTaskContainerUrl } from '../tasksReportUrlUtils.ts';
import {
	toLongIds,
	parseCommaSeparatedValues,
	CURRENT_SPACE_TOKEN,
	SELF_SPACE_TOKEN,
	buildTaskQueryMetadata,
} from '../tasksReportQueryUtils.ts';
import type { TasksReportStatus } from '../tasksReportStatus.ts';

import type { ParsedMacroParams } from './useParsedMacroParams.ts';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface TaskEntity {
	/** Relay node id */
	id: string;
	/** Task id used for row identity and task status mutations. */
	taskId: string | null;
	status: 'COMPLETE' | 'INCOMPLETE' | null;
	/** ADF JSON string for the task body / description */
	adf: string | null;
	dueAt: string | null;
	createdAt: string | null;
	completedAt: string | null;
	assignedTo: {
		accountId: string;
		displayName: string | null;
		avatarPath: string | null;
	} | null;
	createdBy: {
		accountId: string;
		displayName: string | null;
	} | null;
	/** Page or blog post containing the task */
	container: {
		contentId: string | null;
		title: string | null;
		url: string | null;
		spaceUrl: string | null;
		spaceName: string | null;
		labels?: string[];
	} | null;
}

export interface UseTasksReportDataResult {
	tasks: TaskEntity[];
	loading: boolean;
	resolving: boolean;
	error: Error | undefined;
	hasAnyScopeFilter: boolean;
	hasNextPage: boolean;
	loadMore: () => void;
	refetch: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type RawInlineTask = NonNullable<
	NonNullable<NonNullable<GetTasksReportQuery$data['inlineTasks']>['inlineTasks']>[number]
>;

type RawContainerLabel =
	| {
			label?: string | null;
			prefix?: string | null;
	  }
	| null
	| undefined;

function normaliseContainerLabels(
	labels: ReadonlyArray<RawContainerLabel> | null | undefined,
): string[] {
	if (!labels || labels.length === 0) {
		return [];
	}

	const normalisedLabels: string[] = [];
	for (const containerLabel of labels) {
		if (!containerLabel) {
			continue;
		}

		const labelValue = containerLabel.label;
		if (!labelValue) {
			continue;
		}

		normalisedLabels.push(labelValue);
	}

	return normalisedLabels;
}

/** Normalise raw GraphQL task into the public TaskEntity shape. */
function normaliseTask(raw: RawInlineTask): TaskEntity {
	const container = raw.container ?? null;
	let contentId: string | null = null;
	let title: string | null = null;
	let url: string | null = null;
	let spaceUrl: string | null = null;
	let spaceName: string | null = null;
	let labels: string[] = [];

	if (container) {
		if (container.__typename === 'ConfluencePage') {
			contentId = container.pageId ?? null;
			title = container.title ?? null;
			url = buildTaskContainerUrl(container.links?.base, container.links?.webUi);
			spaceName = container.space?.name ?? null;
			spaceUrl = buildTaskContainerUrl(container.space?.links?.base, container.space?.links?.webUi);
			labels = normaliseContainerLabels(container.labels);
		} else if (container.__typename === 'ConfluenceBlogPost') {
			contentId = container.blogPostId ?? null;
			title = container.title ?? null;
			url = buildTaskContainerUrl(container.links?.base, container.links?.webUi);
			spaceName = container.space?.name ?? null;
			spaceUrl = buildTaskContainerUrl(container.space?.links?.base, container.space?.links?.webUi);
			labels = normaliseContainerLabels(container.labels);
		}
	}

	let normalisedStatus: TaskEntity['status'] = null;
	if (raw.status === 'COMPLETE') {
		normalisedStatus = 'COMPLETE';
	} else if (raw.status === 'INCOMPLETE') {
		normalisedStatus = 'INCOMPLETE';
	}

	return {
		id: raw.id,
		// taskId comes back as a string ID and is reused by the completion mutation.
		taskId: raw.taskId ?? null,
		status: normalisedStatus,
		adf: raw.body?.adf ?? null,
		dueAt: raw.dueAt ?? null,
		createdAt: raw.createdAt ?? null,
		completedAt: raw.completedAt ?? null,
		assignedTo: raw.assignedTo
			? {
					accountId: raw.assignedTo.accountId,
					displayName: raw.assignedTo.displayName ?? null,
					avatarPath: raw.assignedTo.profilePicture?.path ?? null,
				}
			: null,
		createdBy: raw.createdBy
			? {
					accountId: raw.createdBy.accountId,
					displayName: raw.createdBy.displayName ?? null,
				}
			: null,
		container: container ? { contentId, title, url, spaceUrl, spaceName, labels } : null,
	};
}

function matchesRequestedStatus(
	task: TaskEntity,
	requestedStatus: TasksReportStatus | undefined,
): boolean {
	if (task.status === null) return true;

	if (!requestedStatus) return true;

	if (requestedStatus === 'complete') {
		return task.status === 'COMPLETE';
	}

	return task.status === 'INCOMPLETE';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseTasksReportDataProps {
	params: ParsedMacroParams;
	/** Optional Confluence cloud ID used for ARI generation on the `id` field. */
	cloudId?: string;
}

/**
 * Fetches inline tasks via the `inlineTasks` AGG GraphQL query.
 *
 * Uses `useQueryInterop` — the Apollo/Relay interop shim — which transparently
 * routes to Apollo or Relay depending on the active build variant, exactly as
 * the Tasks Home feature does.
 *
 * Uses cursor-based pagination: each request fetches `pageSize` tasks. Sorting
 * is delegated to the server via `sortParameters`. Calling `loadMore` appends
 * the next cursor page to the accumulated task list.
 */
export function useTasksReportData({
	params,
	cloudId,
}: UseTasksReportDataProps): UseTasksReportDataResult {
	// Include sortBy and reverseSort so a sort change triggers a new server request
	// with the correct sortParameters rather than a client-side re-sort.
	const queryParams = useMemo(
		() => ({
			columns: params.columns,
			spaces: params.spaces,
			pages: params.pages,
			assignees: params.assignees,
			creators: params.creators,
			labels: params.labels,
			status: params.status,
			duedateFrom: params.duedateFrom,
			duedateTo: params.duedateTo,
			createddateFrom: params.createddateFrom,
			createddateTo: params.createddateTo,
			pageSize: params.pageSize,
			sortBy: params.sortBy,
			reverseSort: params.reverseSort,
		}),
		[
			params.columns,
			params.assignees,
			params.createddateFrom,
			params.createddateTo,
			params.creators,
			params.duedateFrom,
			params.duedateTo,
			params.labels,
			params.pageSize,
			params.pages,
			params.reverseSort,
			params.sortBy,
			params.spaces,
			params.status,
		],
	);

	const rawSpaceId = useSpaceId();
	const currentSpaceId = useMemo(() => {
		if (!rawSpaceId) {
			return undefined;
		}

		const parsedSpaceId = Number.parseInt(rawSpaceId, 10);
		return Number.isNaN(parsedSpaceId) ? undefined : parsedSpaceId;
	}, [rawSpaceId]);
	const explicitSpaces = useMemo(() => parseCommaSeparatedValues(params.spaces), [params.spaces]);

	// Split the explicit spaces param into two buckets:
	// - spaceIds: tokens (@self, currentSpace()) and numeric IDs — resolved synchronously
	// - spaceKeys: human-readable keys (e.g. "ENG", "~DS") — passed directly to the
	//   AGG query which resolves them server-side (InlineTasksByMetadata.spaceKeys field)
	const spaceIds = useMemo(
		() => toLongIds(explicitSpaces, currentSpaceId),
		[explicitSpaces, currentSpaceId],
	);
	const spaceKeys = useMemo(
		() =>
			(explicitSpaces ?? []).filter(
				(value) =>
					value !== CURRENT_SPACE_TOKEN &&
					value !== SELF_SPACE_TOKEN &&
					!/^\d+$/.test(value.startsWith('~') ? value.slice(1) : value),
			),
		[explicitSpaces],
	);

	const pageIds = useMemo(() => toLongIds(parseCommaSeparatedValues(params.pages)), [params.pages]);
	const hasAnyScopeFilter = spaceIds.length > 0 || spaceKeys.length > 0 || pageIds.length > 0;

	// Memoize the first-page taskQuery. Sort changes (queryParams) reset the cursor
	// to null so the server re-runs the query from the beginning with the new order.
	const taskQuery = useMemo(
		() => buildTaskQueryMetadata(queryParams, spaceIds, spaceKeys, null),
		[queryParams, spaceIds, spaceKeys],
	);
	const { data, loading, error, fetchMore, refetch } = useQueryInterop<
		GetTasksReportQuery$data,
		GetTasksReportQuery$variables
	>(GetTasksReportQuery, {
		variables: { taskQuery, cloudId },
		skip: !hasAnyScopeFilter,
		fetchPolicy: 'cache-first',
		notifyOnNetworkStatusChange: true,
	});

	const rawTasks = useMemo(
		() => data?.inlineTasks?.inlineTasks ?? [],
		[data?.inlineTasks?.inlineTasks],
	);
	const safePageSize = queryParams.pageSize > 0 ? queryParams.pageSize : 1;
	const lastFetchedBatchSize = useMemo(() => {
		if (rawTasks.length === 0) {
			return 0;
		}

		const remainder = rawTasks.length % safePageSize;
		return remainder === 0 ? safePageSize : remainder;
	}, [rawTasks.length, safePageSize]);

	// A non-null endCursor means the server has more pages beyond what we've received.
	// The AGG schema does not expose pageInfo.hasNextPage, so endCursor is the proxy.
	const endCursor = data?.inlineTasks?.endCursor ?? null;
	// Guard against inconsistent payloads: if the most recently fetched batch
	// is smaller than pageSize, there is no additional page to fetch.
	const hasNextPage = endCursor !== null && lastFetchedBatchSize >= safePageSize;

	// Keep a local status guard so the table stays correct even if the server/cache
	// transiently returns mixed statuses while filters are switching.
	const tasks = useMemo<TaskEntity[]>(() => {
		const normalizedTasks: TaskEntity[] = [];
		for (const rawTask of rawTasks) {
			if (!rawTask) {
				continue;
			}

			const task = normaliseTask(rawTask);
			if (matchesRequestedStatus(task, params.status)) {
				normalizedTasks.push(task);
			}
		}

		return normalizedTasks;
	}, [rawTasks, params.status]);

	// Fetch the next cursor page and append its tasks to the accumulated list.
	const loadMore = useCallback(() => {
		if (!endCursor || !fetchMore || !hasAnyScopeFilter) {
			return;
		}

		void fetchMore({
			variables: {
				taskQuery: buildTaskQueryMetadata(queryParams, spaceIds, spaceKeys, endCursor),
				cloudId,
			},
			updateQuery(
				previousResult: GetTasksReportQuery$data,
				{ fetchMoreResult }: { fetchMoreResult?: GetTasksReportQuery$data },
			): GetTasksReportQuery$data {
				if (!fetchMoreResult?.inlineTasks) {
					return previousResult;
				}

				const previousTasks = previousResult?.inlineTasks?.inlineTasks ?? [];
				const newTasks = fetchMoreResult.inlineTasks.inlineTasks ?? [];

				return {
					...previousResult,
					inlineTasks: {
						...fetchMoreResult.inlineTasks,
						inlineTasks: [...previousTasks, ...newTasks],
					},
				};
			},
		});
	}, [cloudId, endCursor, fetchMore, hasAnyScopeFilter, queryParams, spaceIds, spaceKeys]);

	const refetchTasks = useCallback(async () => {
		if (!hasAnyScopeFilter) {
			return;
		}

		const loadedPageCount = Math.max(1, Math.ceil(rawTasks.length / safePageSize));
		const first = loadedPageCount * safePageSize;

		await refetch({
			taskQuery: {
				...buildTaskQueryMetadata(queryParams, spaceIds, spaceKeys, null),
				first,
			},
			cloudId,
		});
	}, [
		cloudId,
		hasAnyScopeFilter,
		queryParams,
		rawTasks.length,
		refetch,
		safePageSize,
		spaceIds,
		spaceKeys,
	]);

	return {
		tasks,
		loading,
		resolving: false,
		error: error as Error | undefined,
		hasAnyScopeFilter,
		hasNextPage,
		loadMore,
		refetch: refetchTasks,
	};
}
